import { IconCheck } from '@tabler/icons-react';
import { type FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Dropdown, type DropdownOption, Segmented } from '@/components/common';
import type { EntryKind } from '@/types/api';
import {
  CODES,
  type Code,
  codeLabel,
  isPatientRight,
  isPlanned,
  isPrimary,
  isUpper,
  isValidTooth,
  LOWER_PERMANENT,
  LOWER_PRIMARY,
  MATERIAL_CODES,
  MATERIALS,
  type Material,
  materialLabel,
  normalizeSurfaces,
  plannedPrefix,
  positionOf,
  SURFACE_CODES,
  type Surface,
  stripPlanned,
  UPPER_PERMANENT,
  UPPER_PRIMARY,
} from '@/utils/dental';
import { FINDING_CODES } from '@/utils/entryKind';
import { SurfacePicker } from './SurfacePicker';

export type EntryValues = {
  tooth: number;
  code: Code;
  surfaces: Surface[];
  material: Material | null;
  note: string | null;
  /** Existing state or performed work; a finding is always existing. */
  kind: EntryKind;
};

const COMMON_CODES: Code[] = [
  'caries',
  'filling',
  'crown',
  'root_canal',
  'missing',
  'extraction',
  'implant',
  'sound',
];

const ALL_TEETH = [...UPPER_PERMANENT, ...LOWER_PERMANENT, ...UPPER_PRIMARY, ...LOWER_PRIMARY];

type Props = {
  initial: Omit<Partial<EntryValues>, 'tooth'> & { tooth: number | null };
  /** Kind for a new procedure when `initial` has none (the chart's mode). */
  defaultKind?: EntryKind;
  submitLabel: string;
  busy?: boolean;
  onSubmit: (values: EntryValues) => void;
};

function splitNote(note: string | null | undefined): { planned: boolean; text: string } {
  if (!note) return { planned: false, text: '' };
  return { planned: isPlanned(note), text: stripPlanned(note) };
}

/** Teeth grouped by quadrant ("Felső jobb"), each with its name as a hint. */
function useToothOptions(): DropdownOption<number>[] {
  const { t } = useTranslation();
  return useMemo(
    () =>
      ALL_TEETH.map((tooth) => {
        const primary = isPrimary(tooth);
        const vertical = t(isUpper(tooth) ? 'domain:tooth.upper' : 'domain:tooth.lower');
        const side = t(isPatientRight(tooth) ? 'domain:tooth.right' : 'domain:tooth.left');
        const quadrant = `${vertical.charAt(0).toUpperCase()}${vertical.slice(1)} ${side}`;
        return {
          value: tooth,
          label: String(tooth),
          hint: t(`domain:tooth.${primary ? 'primary' : 'permanent'}.${positionOf(tooth)}`),
          group: primary ? `${quadrant} · ${t('chart.primary')}` : quadrant,
        };
      }),
    [t]
  );
}

export function EntryForm({
  initial,
  defaultKind = 'PERFORMED',
  submitLabel,
  busy,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const [tooth, setTooth] = useState<number | null>(initial.tooth);
  const toothOptions = useToothOptions();
  const [code, setCode] = useState<Code | null>(initial.code ?? null);
  const [surfaces, setSurfaces] = useState<Surface[]>(initial.surfaces ?? []);
  const [material, setMaterial] = useState<Material | null>(initial.material ?? null);
  const initialNote = splitNote(initial.note);
  const [planned, setPlanned] = useState(initialNote.planned);
  const [note, setNote] = useState(initialNote.text);
  const [showAll, setShowAll] = useState(!!initial.code && !COMMON_CODES.includes(initial.code));
  const [kind, setKind] = useState<EntryKind>(initial.kind ?? defaultKind);
  const finding = code !== null && FINDING_CODES.has(code);

  const codes = showAll ? CODES : COMMON_CODES;
  const valid = tooth !== null && isValidTooth(tooth) && code !== null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || tooth === null || code === null) return;
    const text = note.trim();
    const fullNote = [planned ? plannedPrefix() : '', text].filter(Boolean).join(' · ');
    onSubmit({
      tooth,
      code,
      surfaces: SURFACE_CODES.has(code) ? normalizeSurfaces(tooth, surfaces) : [],
      material: MATERIAL_CODES.has(code) ? material : null,
      note: fullNote || null,
      kind: finding ? 'EXISTING' : kind,
    });
  };

  return (
    <form className="stack" onSubmit={submit}>
      <div className="field">
        <label htmlFor="entry-tooth">{t('chart.form.tooth')}</label>
        <Dropdown
          id="entry-tooth"
          value={tooth}
          options={toothOptions}
          placeholder={t('chart.form.chooseTooth')}
          onChange={setTooth}
        />
      </div>

      <div className="field">
        <span className="field__label">{t('chart.form.code')}</span>
        <div className="chips">
          {codes.map((c) => (
            <Chip key={c} pressed={code === c} onClick={() => setCode(c)}>
              {codeLabel(c)}
            </Chip>
          ))}
          {!showAll && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setShowAll(true)}
            >
              {t('chart.form.more')}
            </button>
          )}
        </div>
      </div>

      {code && !finding && (
        <div className="field">
          <span className="field__label">{t('chart.kind.label')}</span>
          <Segmented
            label={t('chart.kind.label')}
            value={kind}
            onChange={setKind}
            options={[
              { value: 'EXISTING', label: t('chart.kind.existing') },
              { value: 'PERFORMED', label: t('chart.kind.performed') },
            ]}
          />
        </div>
      )}

      {code && SURFACE_CODES.has(code) && tooth !== null && (
        <div className="field">
          <span className="field__label">{t('chart.form.surfaces')}</span>
          <div className="row">
            <SurfacePicker tooth={tooth} value={surfaces} onChange={setSurfaces} />
            <p className="muted small grow">
              {surfaces.length
                ? normalizeSurfaces(tooth, surfaces).join('')
                : t('chart.form.noneSelected')}
            </p>
          </div>
        </div>
      )}

      {code && MATERIAL_CODES.has(code) && (
        <div className="field">
          <span className="field__label">{t('chart.form.material')}</span>
          <div className="chips">
            {MATERIALS.map((m) => (
              <Chip
                key={m}
                pressed={material === m}
                onClick={() => setMaterial(material === m ? null : m)}
              >
                {materialLabel(m)}
              </Chip>
            ))}
          </div>
        </div>
      )}

      <div className="row">
        <Chip pressed={planned} onClick={() => setPlanned(!planned)}>
          {plannedPrefix()}
        </Chip>
        <span className="muted small">{t('chart.form.plannedHint')}</span>
      </div>

      <div className="field">
        <label htmlFor="entry-note">{t('chart.form.note')}</label>
        <input
          id="entry-note"
          className="input"
          maxLength={400}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      {/* Always in view on a long form, bottom right like the dictation buttons. */}
      <div className="sheet-fab">
        <button
          type="submit"
          className="fab__btn fab__btn--save"
          disabled={!valid || busy}
          aria-label={submitLabel}
          title={submitLabel}
        >
          <IconCheck size={26} />
        </button>
      </div>
    </form>
  );
}
